import React, { useState, useEffect } from 'react';
import axios from 'axios';

function StudentDeviceMonitor({ userRole, userClass, onClose }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalStudents, setTotalStudents] = useState(0);
  const [blockedDevices, setBlockedDevices] = useState([]);
  const [showBlockedDevices, setShowBlockedDevices] = useState(false);

  useEffect(() => {
    fetchSessions();
    fetchBlockedDevices();
  }, []);

  const fetchSessions = async () => {
    try {
      let response;
      if (userRole === 'admin' || userRole === 'principal') {
        // Admin can see all student sessions
        response = await axios.get('/api/all-student-sessions');
        setSessions(response.data.sessions);
      } else if (userRole === 'teacher' && userClass) {
        // Teacher can see only their class sessions
        response = await axios.get(`/api/class-student-sessions/${userClass}`);
        setSessions(response.data.sessions);
        setTotalStudents(response.data.totalStudents);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockedDevices = async () => {
    try {
      const className = userRole === 'teacher' ? userClass : 'all';
      const response = await axios.get(`/api/blocked-devices?className=${className}`);
      setBlockedDevices(response.data.blockedDevices);
    } catch (error) {
      console.error('Error fetching blocked devices:', error);
    }
  };

  const terminateSession = async (userId, deviceId, studentName) => {
    // First ask what action to take
    const blockDevice = window.confirm(
      `What would you like to do with ${studentName}'s device?\n\n` +
      `Click OK to BLOCK device (student cannot log back in)\n` +
      `Click Cancel to just TERMINATE session (student can log back in)`
    );
    
    const reason = blockDevice ? 
      prompt('Reason for blocking device (optional):') || 'Blocked by administrator' :
      'Session terminated';
    
    // Proceed with the action
    try {
      await axios.delete(`/api/student-sessions/${userId}/${deviceId}`, {
        data: {
          terminatedBy: userRole === 'teacher' ? `Teacher (${userClass})` : 'Administrator',
          blockDevice: blockDevice,
          reason: reason,
          forceLogout: true // Flag to indicate immediate logout required
        }
      });
      
      fetchSessions(); // Refresh the list
      fetchBlockedDevices(); // Refresh blocked devices
      
      if (blockDevice) {
        alert(`✅ Device BLOCKED successfully.\n\n${studentName} has been logged out immediately and cannot log in on this device until you reactivate it.`);
      } else {
        alert(`✅ Session TERMINATED successfully.\n\n${studentName} has been logged out immediately and can log back in on this device.`);
      }
    } catch (error) {
      console.error('Error terminating session:', error);
      alert('❌ Failed to terminate session');
    }
  };

  const reactivateDevice = async (userId, deviceId, studentName) => {
    if (window.confirm(`Reactivate device access for ${studentName}?\n\nThis will allow them to log in on this device again.`)) {
      try {
        await axios.put(`/api/reactivate-device/${userId}/${deviceId}`, {
          reactivatedBy: userRole === 'teacher' ? `Teacher (${userClass})` : 'Administrator'
        });
        fetchBlockedDevices(); // Refresh blocked devices
        alert(`✅ Device access reactivated for ${studentName}`);
      } catch (error) {
        console.error('Error reactivating device:', error);
        alert('❌ Failed to reactivate device');
      }
    }
  };

  const forceUnblockDevice = async (deviceId) => {
    try {
      await axios.delete(`/api/force-unblock-device/${deviceId}`, {
        data: {
          unblockedBy: userRole === 'teacher' ? `Teacher (${userClass})` : 'Administrator'
        }
      });
      fetchBlockedDevices(); // Refresh blocked devices
      alert(`✅ Device ${deviceId} has been unblocked`);
    } catch (error) {
      console.error('Error force unblocking device:', error);
      alert('❌ Failed to unblock device');
    }
  };

  const showAllBlockedDevices = async () => {
    try {
      console.log('Fetching all blocked devices...');
      const response = await axios.get('/api/blocked-devices?className=all');
      console.log('Response:', response.data);
      
      const allBlocked = response.data.blockedDevices || [];
      
      if (allBlocked.length === 0) {
        alert(`No blocked devices found.\n\nTotal in system: ${response.data.total || 0}`);
        return;
      }
      
      let deviceList = `All Blocked Devices (${allBlocked.length} found):\n\n`;
      allBlocked.forEach((device, index) => {
        deviceList += `${index + 1}. ${device.userId?.name || 'Unknown'} (${device.userId?.class || 'N/A'})\n`;
        deviceList += `   Device ID: ${device.deviceId}\n`;
        deviceList += `   Blocked: ${device.blockedAt ? new Date(device.blockedAt).toLocaleString() : 'Unknown'}\n\n`;
      });
      
      const choice = prompt(deviceList + 'Enter Device ID to unblock (copy from above):');
      if (choice && choice.trim()) {
        forceUnblockDevice(choice.trim());
      }
    } catch (error) {
      console.error('Error fetching all blocked devices:', error);
      alert(`❌ Failed to fetch blocked devices\n\nError: ${error.response?.data?.error || error.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getDeviceInfo = (userAgent) => {
    if (!userAgent) return 'Unknown Device';
    
    if (userAgent.includes('Mobile') || userAgent.includes('Android')) return '📱 Mobile';
    if (userAgent.includes('iPad')) return '📱 iPad';
    if (userAgent.includes('iPhone')) return '📱 iPhone';
    if (userAgent.includes('Chrome')) return '💻 Chrome';
    if (userAgent.includes('Firefox')) return '💻 Firefox';
    if (userAgent.includes('Safari')) return '💻 Safari';
    return '💻 Desktop';
  };

  const getSessionDuration = (loginTime) => {
    const now = new Date();
    const login = new Date(loginTime);
    const diffMs = now - login;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMins}m`;
    }
    return `${diffMins}m`;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
      justifyContent: 'center',
      zIndex: 1000,
      paddingTop: window.innerWidth <= 768 ? '20px' : '0'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '15px',
        padding: '25px',
        maxWidth: '900px',
        width: '95%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #e9ecef',
          paddingBottom: '15px'
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#1e40af', fontSize: '22px' }}>
              📱 Student Device Monitor
            </h3>
            <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: '14px' }}>
              {userRole === 'teacher' ? `Class: ${userClass}` : 'All Classes'} 
              {totalStudents > 0 && ` • ${totalStudents} total students`}
            </p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                onClick={() => setShowBlockedDevices(false)}
                style={{
                  background: !showBlockedDevices ? '#10b981' : '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                🟢 Active Sessions ({sessions.length})
              </button>
              <button
                onClick={() => setShowBlockedDevices(true)}
                style={{
                  background: showBlockedDevices ? '#dc2626' : '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                🔴 Blocked Devices ({blockedDevices.length})
              </button>
              <button
                onClick={showAllBlockedDevices}
                style={{
                  background: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                🔧 Show All Blocked
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
            <p>Loading device sessions...</p>
          </div>
        ) : (
          <div>
            <div style={{
              background: '#f8f9fa',
              padding: '15px',
              borderRadius: '10px',
              marginBottom: '20px',
              border: '1px solid #dee2e6',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '15px'
            }}>
              <div>
                <strong>Active Sessions:</strong> {sessions.length}
              </div>
              <div>
                <strong>Blocked Devices:</strong> {blockedDevices.length}
              </div>
              <div>
                <strong>Unique Students:</strong> {new Set([...sessions.map(s => s.userId?._id), ...blockedDevices.map(d => d.userId?._id)]).size}
              </div>
              <div>
                <strong>Device Limit:</strong> 2 per student
              </div>
            </div>

            {!showBlockedDevices ? (
              sessions.length > 0 ? (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {sessions.map((session, index) => (
                    <div key={`${session.userId?._id}-${session.deviceId}`} style={{
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      border: '1px solid #dee2e6',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: '20px',
                        alignItems: 'start'
                      }}>
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '12px'
                          }}>
                            <div style={{
                              background: '#1e40af',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}>
                              👤 {session.userId?.name || 'Unknown Student'}
                            </div>
                            <div style={{
                              background: '#10b981',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              Class: {session.userId?.class || 'N/A'}
                            </div>
                          </div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '10px',
                            fontSize: '14px',
                            color: '#495057'
                          }}>
                            <div>
                              <strong>Device:</strong> {getDeviceInfo(session.userAgent)}
                            </div>
                            <div>
                              <strong>Login Time:</strong> {formatDate(session.loginTime)}
                            </div>
                            <div>
                              <strong>Duration:</strong> {getSessionDuration(session.loginTime)}
                            </div>
                            <div>
                              <strong>Last Activity:</strong> {formatDate(session.lastActivity)}
                            </div>
                            <div>
                              <strong>IP Address:</strong> {session.ipAddress || 'N/A'}
                            </div>
                            <div>
                              <strong>Device ID:</strong> {session.deviceId.substring(0, 12)}...
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{
                            background: '#28a745',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textAlign: 'center'
                          }}>
                            🟢 Active
                          </div>
                          <button
                            onClick={() => terminateSession(session.userId?._id, session.deviceId, session.userId?.name)}
                            style={{
                              background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              boxShadow: '0 2px 8px rgba(220, 53, 69, 0.3)'
                            }}
                          >
                            🚪 Terminate/Block
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '60px',
                  color: '#6c757d'
                }}>
                  <div style={{ fontSize: '64px', marginBottom: '20px' }}>📱</div>
                  <h4 style={{ marginBottom: '10px' }}>No Active Sessions</h4>
                  <p>No students are currently logged in</p>
                </div>
              )
            ) : (
              // Blocked Devices View
              blockedDevices.length > 0 ? (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {blockedDevices.map((device, index) => (
                    <div key={`${device.userId?._id}-${device.deviceId}`} style={{
                      background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                      border: '1px solid #fca5a5',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(220, 53, 69, 0.1)'
                    }}>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: '20px',
                        alignItems: 'start'
                      }}>
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '12px'
                          }}>
                            <div style={{
                              background: '#dc2626',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '20px',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}>
                              👤 {device.userId?.name || 'Unknown Student'}
                            </div>
                            <div style={{
                              background: '#dc2626',
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              Class: {device.userId?.class || 'N/A'}
                            </div>
                          </div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '10px',
                            fontSize: '14px',
                            color: '#495057'
                          }}>
                            <div>
                              <strong>Device:</strong> {getDeviceInfo(device.userAgent)}
                            </div>
                            <div>
                              <strong>Blocked:</strong> {formatDate(device.blockedAt)}
                            </div>
                            <div>
                              <strong>Blocked By:</strong> {device.blockedBy || 'Administrator'}
                            </div>
                            <div>
                              <strong>Reason:</strong> {device.blockedReason || 'No reason provided'}
                            </div>
                            <div>
                              <strong>Last Login:</strong> {formatDate(device.loginTime)}
                            </div>
                            <div>
                              <strong>Device ID:</strong> 
                              <span 
                                style={{ 
                                  background: '#f3f4f6', 
                                  padding: '2px 6px', 
                                  borderRadius: '4px', 
                                  fontFamily: 'monospace',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                                onClick={() => {
                                  navigator.clipboard.writeText(device.deviceId);
                                  alert('Device ID copied to clipboard!');
                                }}
                                title="Click to copy full Device ID"
                              >
                                {device.deviceId}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div style={{
                            background: '#dc2626',
                            color: 'white',
                            padding: '6px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: '600',
                            textAlign: 'center'
                          }}>
                            🔴 Blocked
                          </div>
                          <button
                            onClick={() => reactivateDevice(device.userId?._id, device.deviceId, device.userId?.name)}
                            style={{
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                            }}
                          >
                            ✅ Reactivate
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '60px',
                  color: '#6c757d'
                }}>
                  <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔴</div>
                  <h4 style={{ marginBottom: '10px' }}>No Blocked Devices</h4>
                  <p>No devices have been blocked</p>
                </div>
              )
            )}

            <div style={{
              marginTop: '25px',
              padding: '20px',
              background: '#e3f2fd',
              borderRadius: '12px',
              border: '1px solid #2196f3'
            }}>
              <h4 style={{ margin: '0 0 15px 0', color: '#1976d2', fontSize: '16px' }}>
                ℹ️ Device Management Information
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '15px',
                fontSize: '14px', 
                color: '#495057' 
              }}>
                <div>
                  <strong>Session Limits:</strong>
                  <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
                    <li>Maximum 2 devices per student</li>
                    <li>Auto-expire after 7 days inactivity</li>
                  </ul>
                </div>
                <div>
                  <strong>Monitoring Features:</strong>
                  <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
                    <li>Real-time device tracking</li>
                    <li>Login time & duration</li>
                    <li>IP address logging</li>
                  </ul>
                </div>
                <div>
                  <strong>Admin Actions:</strong>
                  <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
                    <li>Terminate sessions</li>
                    <li>Block/Unblock devices</li>
                    <li>Reactivate blocked access</li>
                  </ul>
                </div>
                <div>
                  <strong>Device Blocking:</strong>
                  <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px' }}>
                    <li>Temporary: Student can log back in</li>
                    <li>Permanent: Requires reactivation</li>
                    <li>Teachers can reactivate devices</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentDeviceMonitor;